import { date } from '@ipfabric/utils';
import type { VerifiedLicense } from '@ipfabric/utils/license/licenseSchema';
import classNames from 'classnames';
import { ButtonLink } from 'components/Button/ButtonLink';
import {
  type GlobalUserState,
  type LicenseState,
  useGlobalStateContextSelectors,
} from 'components/GlobalState/GlobalStateModel';
import { useLocale } from 'containers/App/LocaleContext';
import { Permissions } from 'containers/Permissions/Permissions';
import get from 'lodash/get';
import type { FC } from 'react';
import type { HeaderDropdownTitle } from '../Header';
import './UserDropdown.css';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Link, PageLink } from '@ipfabric/ui-components/Link/Link';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiScopeRequirementMet } from 'utils/permissions/useApiScopeRequirementMet';

interface Props {
  toggleVisibleDropdown: (name: HeaderDropdownTitle) => void;
  user?: GlobalUserState;
  licenseState?: LicenseState;
  licenseValidTo: number | null;
  additionalFeatures?: VerifiedLicense['additionalFeatures'];
  timezone: date.Timezone;
  dateTimeFormat: string;
  licenseDeviceLimit?: number | null;
  licenseCCULimit?: number | null;
  version?: string | null;
}

const isPositiveNumber = (
  value: number | null | undefined,
): value is number => {
  return value !== null && value !== undefined && value >= 0;
};

export const UserDropdown: FC<Props> = (props) => {
  const {
    toggleVisibleDropdown,
    user,
    licenseState,
    licenseValidTo,
    additionalFeatures,
    timezone,
    dateTimeFormat,
    licenseDeviceLimit,
    licenseCCULimit,
    version,
  } = props;

  const apiScopeRequirementMet = useApiScopeRequirementMet();

  const { cloudLicensingSupported: cloudLicensingSupportedByCurrentSnapshot } =
    useGlobalStateContextSelectors({
      cloudLicensingSupported: prop('cloudLicensingSupported'),
    });

  const {
    app: { header: msg },
  } = useLocale();

  const aimFeature = additionalFeatures?.aim;

  return (
    <div className={classNames('dropdown-menu', 'user-dropdown', 'block')}>
      <div className='user-dropdown__header'>
        <img
          alt='User'
          className='user-dropdown__image'
          src='/public/img/IP_Fabric_Icon.svg'
        />
        <div className='user-dropdown__header-right'>
          <div className='text-xs leading-5'>
            {user && (
              <div>
                User: <strong className='break-all'>{user.username}</strong>
              </div>
            )}
            <div
              onClick={() => {
                toggleVisibleDropdown('user-menu');
              }}
            >
              <PageLink to='/me/profile' size='small'>
                {msg.profileSettings}
              </PageLink>
            </div>
          </div>
          <ButtonLink
            variant='secondary'
            to='/logout'
            dataTestId='logout-button'
          >
            <i className='fa-regular fa-right-from-bracket mr-2 text-sm' />
            <strong>{msg.logout}</strong>
          </ButtonLink>
        </div>
      </div>
      <div className='user-dropdown__body'>
        <div className='user-dropdown__license-info'>
          <p>License</p>
          <p>
            {licenseState !== 'VALID' || !licenseValidTo ? (
              <PageLink
                to='/upload-license'
                size='small'
                onClick={() => {
                  toggleVisibleDropdown('user-menu');
                }}
              >
                {msg.licenseExpired}
              </PageLink>
            ) : (
              <span>
                {msg.licenseValidTo.replace(
                  '{date}',
                  date.formatUtcTimestampInTimeZone(
                    licenseValidTo,
                    timezone,
                    dateTimeFormat,
                  ),
                )}
              </span>
            )}
          </p>
          {aimFeature && !aimFeature.expired ? (
            <p>
              AIM valid until{' '}
              {date.formatUtcTimestampInTimeZone(
                aimFeature.validTo,
                timezone,
                dateTimeFormat,
              )}
            </p>
          ) : null}
          {isPositiveNumber(licenseDeviceLimit) ? (
            <p>
              {
                // For some reason, the license CCU limit defaults to 0, even when unused. So we only show the CCU limit if it is non-zero.
                isPositiveNumber(licenseCCULimit) && licenseCCULimit > 0
                  ? `Licensed on-prem/cloud: ${licenseDeviceLimit.toLocaleString()}/${licenseCCULimit.toLocaleString()}`
                  : `Licensed devices: ${licenseDeviceLimit.toLocaleString()} ${licenseDeviceLimit === 1 ? 'device' : 'devices'}`
              }
            </p>
          ) : null}
          {apiScopeRequirementMet({
            method: 'POST',
            path: '/tables/settings/administration/license-consumption/overview',
          }) && cloudLicensingSupportedByCurrentSnapshot ? (
            <Link
              className='mb-2'
              to='/settings/administration/license-consumption/overview'
            >
              Show usage detail <Icon size='xs' glyph='fa-chevron-right' />
            </Link>
          ) : null}
          {version && (
            <p>
              {get(msg, 'nimpeeVersion', '')} <strong>{version}</strong>
            </p>
          )}
        </div>
      </div>
      <div className='user-dropdown__footer'>
        <PageLink
          to='/eula'
          size='small'
          onClick={() => {
            toggleVisibleDropdown('user-menu');
          }}
        >
          {msg.eula}
        </PageLink>
        <Permissions requiredScope={['settings']}>
          <PageLink
            to='/upload-license'
            size='small'
            onClick={() => {
              toggleVisibleDropdown('user-menu');
            }}
          >
            {msg.uploadLicense}
          </PageLink>
        </Permissions>
      </div>
    </div>
  );
};
